import { useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { VoteStatus } from "../Deliberation/useVoteState";

import DistributionCaseProposal from "./Buttons/DistributionCaseProposal";
import { useBoxPosition } from "./useBoxPosition";
import { getDeliberationOptionColor } from "./services/getDeliberationOptionColor";
import { getRemainingWeight } from "./services/getRemainingWeight";
import { getNbDecimal, roundDecimal } from "components/helper/numberHelper";
import { StyledProposals, Wrapper } from "./VoteDistribution.styles";

type Proposal = {
    disableOtherPropositionsIfSelected: boolean;
    id: string;
    shortText: string;
    isVisibleByDefault: boolean;
};

type Props = {
    propositions: Array<Proposal>;
    answers: Array<{
        id: string;
        weight: number;
    }>;
    isSecret: boolean;
    voterWeight: number;
    voteStatus: string;
    updateWeight: (propositionId: string, weight: number) => void;
    proposalsOrientation: string;
    titleId: string;
    // Set when the user votes for several voters, to name each group after its voter.
    groupVoterName?: string;
};

const VoteDistribution = ({
    propositions,
    answers,
    isSecret,
    voterWeight,
    voteStatus,
    updateWeight,
    proposalsOrientation,
    titleId,
    groupVoterName,
}: Props) => {
    const { t } = useTranslation();
    const hintId = useId();
    const inputRefs = useRef<Array<HTMLInputElement>>([]);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isCompact = "horizontal" === proposalsOrientation;
    const boxPosition = useBoxPosition({
        containerRef: wrapperRef,
        isSideBySide: isCompact,
        optionCount: propositions.length,
    });

    const nbDecimal = getNbDecimal(voterWeight);
    const remainingWeight = getRemainingWeight(voterWeight, answers);

    const renderProposals = () => {
        const filteredInputRefs = inputRefs.current.filter(
            (ref) => ref !== null,
        );

        const focusNextInput = (currentInputId: string) => {
            const currentProposal = propositions.find(
                (proposal) => proposal.id === currentInputId,
            );

            if (!currentProposal) {
                return;
            }
            const indexOfCurrentProposal =
                propositions.indexOf(currentProposal);

            const nextInput =
                filteredInputRefs[
                    indexOfCurrentProposal === filteredInputRefs.length - 1
                        ? 0
                        : indexOfCurrentProposal + 1
                ];

            nextInput?.focus();
        };

        return propositions.map((proposition, item) => {
            const handleBlur = (inputValue: number) => {
                const currentAnswerWeight = answers.find(
                    (answer) => answer.id === proposition.id,
                )?.weight as number;

                const maxValue: number = roundDecimal(
                    remainingWeight + currentAnswerWeight,
                    nbDecimal,
                );

                const newWeight =
                    inputValue >= maxValue ? maxValue : inputValue;

                updateWeight(proposition.id, newWeight);
            };

            const isActive =
                VoteStatus.Success === voteStatus && isSecret
                    ? false
                    : answers.some(
                          (answer) =>
                              answer.id === proposition.id && answer.weight > 0,
                      );

            const isDisabled = () => {
                if (VoteStatus.Success === voteStatus) {
                    return true;
                }

                if (isActive) {
                    return false;
                }

                if (0 === remainingWeight) {
                    return true;
                }

                return false;
            };

            return (
                <DistributionCaseProposal
                    key={item}
                    isActive={isActive}
                    text={proposition.shortText}
                    isDisabled={isDisabled()}
                    remainingWeight={remainingWeight}
                    handleBlur={handleBlur}
                    color={getDeliberationOptionColor(proposition.id)}
                    isCompact={isCompact}
                    boxPosition={boxPosition}
                    value={
                        answers.find((answer) => answer.id === proposition.id)
                            ?.weight
                    }
                    handleKeyUp={(e) => {
                        if ("Enter" === e.key) {
                            focusNextInput(proposition.id);
                        }
                    }}
                    currentRef={(el) =>
                        (inputRefs.current[Number(proposition.id)] = el)
                    }
                    nbDecimal={nbDecimal}
                    hintId={hintId}
                />
            );
        });
    };

    return (
        <Wrapper ref={wrapperRef}>
            {/* In case of proxies, the position of proposals is horizontal */}
            <StyledProposals
                $proposalsOrientation={proposalsOrientation}
                titleId={titleId}
                voterName={groupVoterName}
            >
                {renderProposals()}
            </StyledProposals>
            <span id={hintId} hidden>
                {t("distributionEnterHint")}
            </span>
        </Wrapper>
    );
};

export default VoteDistribution;
